import { colors } from '@smartsheet/lodestar-core';
import { Icon } from '@smartsheet/lodestar-icons';
import React, { useState } from 'react';
import { Handle, NodeProps, Position } from 'react-flow-renderer';
import { useDispatch, useSelector } from 'react-redux';
import {
  GraphNodeTypes,
  selectedElementSelector,
} from '../../../../services/ducks/designerDuck/designerDuck';
import { WorkflowStateNode } from '../../../../services/ducks/workflowDuck/workflowDuck';
import { NODE_HEIGHT } from '../../../../utils/dimensions';
import { useCanNodeBeDeleted } from '../../../../utils/useCanNodeBeDeleted';
import { DragHandleIcon } from '../../../icons/DragHandleIcon';
import { StateIcon } from '../../../icons/StateIcon';
import { RemoveNodeButton } from './RemoveNodeButton';
import { IconBox, Label, Node } from './styled';
import { getDragEndHandler, getDragStartHandler, onDragHandler } from './utils';

type StateNodeData = {
  data: WorkflowStateNode;
  label: JSX.Element;
} & {
  data: {
    type: GraphNodeTypes.STATE;
  };
};

// A State has a target handle and a source handle
export const State = ({
  data,
  forReactFlow = true,
}: NodeProps<StateNodeData> & { forReactFlow: boolean }) => {
  const [hover, setHover] = useState<boolean>(false);
  const dispatch = useDispatch();
  const selectedElement = useSelector(selectedElementSelector);
  const canNodeBeDeleted = useCanNodeBeDeleted(data.data.id, data.data.type);

  return (
    <Node
      selected={selectedElement?.id === data.data.id}
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onDragStart={getDragStartHandler(dispatch, GraphNodeTypes.STATE, {
        __type: 'existing',
        ...data.data,
        type: GraphNodeTypes.STATE,
      })}
      onDrag={onDragHandler}
      onDragEnd={getDragEndHandler(dispatch)}
      draggable={true}
    >
      {forReactFlow ? (
        <Handle
          type="target"
          position={Position.Top}
          isConnectable={true}
          style={{ visibility: 'hidden', left: NODE_HEIGHT / 2 }}
        />
      ) : null}
      <IconBox color={colors.visBerryLight10} hover={hover}>
        <Icon
          svgComponent={hover ? DragHandleIcon : StateIcon}
          size={'xxSmall'}
        />
      </IconBox>
      <Label selected={selectedElement?.id === data.data.id}>
        {data.label}
      </Label>
      {forReactFlow ? (
        <Handle
          type="source"
          position={Position.Bottom}
          isConnectable={true}
          style={{ visibility: 'hidden', left: NODE_HEIGHT / 2 }}
        />
      ) : null}
      {(hover || selectedElement?.id === data.data.id) && canNodeBeDeleted ? (
        <RemoveNodeButton node={data.data} />
      ) : null}
    </Node>
  );
};
